import { connect, FormikValues } from 'formik'
import { object } from 'yup'

import Label from '@/components/Label'
import CodeEditor from '@/pages/SpaceAppBuilder/components/CodeEditor/CodeEditor'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { AdaptiveCardStepData, FieldTypes, StepData } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import cn from '@/utilities/cn'

import adaptiveCardSchema from './adaptive-card-schema.json'

type ScriptProps = {
  readOnly?: boolean
}

const getValidationSchema = () => {
  return {
    payload: object()
      .required('Payload is required')
      .typeError(({ originalValue }) => {
        try {
          JSON.parse(originalValue)
        } catch {
          return 'Please enter valid JSON'
        }
        return true
      })
  }
}

const getInitialValues = (stepData: AdaptiveCardStepData): StepData => ({
  payload: stepData.payload,
  data: stepData.data
})

const TextBlock = ({ children, className, ...rest }: any) => (
  <p className={cn('mt-2 text-sm', className)} {...rest}>
    {children}
  </p>
)
const Link = ({ children, className, ...rest }: any) => (
  <a className={className} target="_blank" rel="noreferrer" {...rest}>
    {children}
  </a>
)

const AdaptiveCardOperation = ({ formik, readOnly = false }: ScriptProps & FormikValues) => {
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const { values, setFieldValue, errors } = formik

  return (
    <>
      <TextBlock>
        To design an adaptive card, refer to the{' '}
        <Link href="https://adaptivecards.io/designer/">Adaptive Card Designer</Link> and{' '}
        <Link href="https://adaptivecards.io/explorer/"> Schema Explorer</Link>.
      </TextBlock>
      <div className="mt-6 rounded-md border px-2 pb-2 shadow-sm">
        <CodeEditor
          name="stepData.payload"
          label="Payload"
          required
          description={
            <TextBlock className="mt-0 mb-2">
              Specify the adaptive card's layout. Using{' '}
              <Link href="https://learn.microsoft.com/en-us/adaptive-cards/templating/language">template language</Link>
              , the payload can reference information from the data object specified at the bottom of the panel.
            </TextBlock>
          }
          readOnly={readOnly}
          setFieldValue={setFieldValue}
          // Allow save if there are adaptive card schema type errors since data type values are currently all read as strings
          setFieldError={() => {}}
          editorErrorAsWarning={true}
          defaultValue={
            typeof values?.stepData?.payload === 'string'
              ? values.stepData.payload
              : JSON.stringify(values.stepData.payload)
          }
          errors={errors}
          schemas={[{ schema: adaptiveCardSchema, uri: 'http://adaptivecards.io/schemas/adaptive-card.json' }]}
        />
      </div>
      <div className="mt-6">
        <Label>Data</Label>
        <TextBlock className="mt-0 mb-2">Specify the adaptive card's data.</TextBlock>
        <VariableInputContainer className="mb-6 w-full">
          <VariableInput
            className="max-w-full"
            readOnly={readOnly}
            name="stepData.data"
            variables={variables}
            validVariableTypes={[FieldTypes.OBJECT, FieldTypes.ANY]}
            placeholder="Enter the adaptive card data"
          />
        </VariableInputContainer>
      </div>
    </>
  )
}

export default connect(AdaptiveCardOperation)
export { getValidationSchema, getInitialValues }
